/**
 * useSubtitleControlState
 * 
 * 职责：抽离字幕业务逻辑，提供字幕控制相关的状态和方法
 * - 判断是否可以重复当前字幕
 * - 判断是否可以词级对齐
 * - 字幕显示模式管理
 * - 重复当前字幕功能
 */

import { useMemo, useCallback } from 'react';
import type { PlayerActions } from './usePlayerEngine';

export type SubtitleViewMode = 'both' | 'original' | 'translated';

interface UseSubtitleControlStateParams {
  currentSubtitleIndex: number;
  originalSubtitles: Array<{ start?: number; end?: number; text?: string }>;
  translatedSubtitles: Array<{ start?: number; end?: number; text?: string }>;
  // 当前生效的词级段列表（来自 display_words 或 words 的规范化结果），用于在进入重复模式时把词索引重置到第一词
  wordSegments: Array<{ displayWords?: unknown[] }> | null;
  enableWordAlignment: boolean;
  hasWordAlignment: boolean; // 从 descriptor.capabilities.hasWordAlignment 获取
  subtitleViewMode: SubtitleViewMode;
  setSubtitleViewMode: (mode: SubtitleViewMode | ((prev: SubtitleViewMode) => SubtitleViewMode)) => void;
  // 重复模式相关状态（由主组件管理）
  isRepeatMode: boolean;
  setIsRepeatMode: (value: boolean | ((prev: boolean) => boolean)) => void;
  repeatSubtitleIndex: number;
  setRepeatSubtitleIndex: (value: number | ((prev: number) => number)) => void;
  // 词索引相关状态（由主组件管理）
  setCurrentWordIndex: (value: { segmentIndex: number; wordIndex: number } | null | ((prev: { segmentIndex: number; wordIndex: number } | null) => { segmentIndex: number; wordIndex: number } | null)) => void;
  // 播放器 actions（用于 seek 等操作）
  playerActions: PlayerActions;
}

export function useSubtitleControlState({
  currentSubtitleIndex,
  originalSubtitles,
  translatedSubtitles,
  wordSegments,
  enableWordAlignment,
  hasWordAlignment,
  subtitleViewMode,
  setSubtitleViewMode,
  isRepeatMode,
  setIsRepeatMode,
  repeatSubtitleIndex,
  setRepeatSubtitleIndex,
  setCurrentWordIndex,
  playerActions,
}: UseSubtitleControlStateParams) {
  // 判断是否可以重复当前字幕
  const canRepeatCurrentCue = useMemo(() => {
    return currentSubtitleIndex >= 0 && 
           (originalSubtitles.length > 0 || translatedSubtitles.length > 0);
  }, [currentSubtitleIndex, originalSubtitles.length, translatedSubtitles.length]);

  // 判断是否可以词级对齐：基于 descriptor.capabilities.hasWordAlignment
  // （表示视频至少有一份词级数据：display_words.json 或 words.json）
  const canWordAlign = useMemo(() => {
    return hasWordAlignment;
  }, [hasWordAlignment]);

  // 字幕显示模式标签
  const subtitleModeLabel = useMemo(() => {
    switch (subtitleViewMode) {
      case 'both':
        return '全';
      case 'original':
        return '原';
      case 'translated':
        return '译';
      default:
        return '全';
    }
  }, [subtitleViewMode]);

  // 切换字幕显示模式
  const toggleSubtitleMode = useCallback(() => {
    setSubtitleViewMode(prev =>
      prev === 'both'
        ? 'original'
        : prev === 'original'
          ? 'translated'
          : 'both'
    );
  }, [setSubtitleViewMode]);

  // 重复当前字幕功能（从 VideoPlayerWithSubtitles 迁移而来）
  const repeatCurrentCue = useCallback(() => {
    if (!isRepeatMode) {
      // 开启重复模式：锁定当前字幕索引
      if (currentSubtitleIndex >= 0) {
        setRepeatSubtitleIndex(currentSubtitleIndex);
        setIsRepeatMode(true);
        // 立即跳回到锁定的字幕开始时间
        const original = originalSubtitles[currentSubtitleIndex];
        const translated = translatedSubtitles[currentSubtitleIndex];
        if (original || translated) {
          const start = (original && original.start !== undefined) ? original.start :
                       (translated && translated.start !== undefined) ? translated.start : 0;
          // 使用 player actions 进行 seek，不允许直接操作 videoRef
          playerActions.seek(start, { autoPlayAfterSeek: false });
        }
        // 如果有词级对齐数据且开启了词级对齐，将词索引设置为该字幕的第一个词
        if (enableWordAlignment && wordSegments && wordSegments.length > 0 &&
            currentSubtitleIndex < wordSegments.length) {
          setCurrentWordIndex({ segmentIndex: currentSubtitleIndex, wordIndex: 0 });
        }
      }
    } else {
      // 关闭重复模式：清除锁定的字幕索引
      setIsRepeatMode(false);
      setRepeatSubtitleIndex(-1);
    }
  }, [
    isRepeatMode,
    currentSubtitleIndex,
    originalSubtitles,
    translatedSubtitles,
    enableWordAlignment,
    wordSegments,
    setIsRepeatMode,
    setRepeatSubtitleIndex,
    setCurrentWordIndex,
    playerActions,
  ]);

  return {
    canRepeatCurrentCue,
    canWordAlign,
    subtitleMode: subtitleViewMode,
    subtitleModeLabel,
    toggleSubtitleMode,
    repeatCurrentCue,
  };
}

